import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpLeft, ArrowUpRight, Check, UserPlus, Users } from 'lucide-react';
import { profilesApi } from '../../api/profiles';
import { contactsApi } from '../../api/contacts';
import { useAuth } from '../../contexts/AuthContext';
import { useFollowCooldown, followWaitLabel } from '../../services/followCooldown';
import FramedAvatar from '../FramedAvatar';
import DetailDialog from '../DetailDialog/DetailDialog';
import ProfileWallet from './ProfileWallet';
import PreviewBadges from './PreviewBadges';

function Preview({ person, theme, language, onClose, onOpenProfile, actor }) {
  const [profile, setProfile] = useState(null), [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const active = useRef(false), pending = useRef(false);
  const wait = useFollowCooldown(), ar = language === 'ar', id = person.userId || person.id;
  useEffect(() => {
    active.current = true;
    profilesApi.preview(id).then(value => { if (active.current) setProfile(value); }).catch(() => { if (active.current) setError(ar ? 'تعذر تحميل تفاصيل الملف الآن.' : 'Profile details are unavailable right now.'); });
    return () => { active.current = false; };
  }, [id, ar]);
  const value = profile || person, name = value.displayName || value.name || value.userHandle || (ar ? 'مستخدم' : 'User');
  const action = async kind => {
    if (pending.current || !profile) return;
    pending.current = true; setBusy(true); setError('');
    try {
      let changes;
      if (kind === 'follow') {
        if (!profile.followedByMe && wait) return;
        await (profile.followedByMe ? profilesApi.unfollow(profile.id) : profilesApi.follow(profile.id));
        changes = { followedByMe: !profile.followedByMe };
      } else {
        if (profile.contact || profile.contactRequestPending) return;
        try { await contactsApi.sendRequest(profile.id); changes = { contactRequestPending: true }; }
        catch (failure) {
          if (failure.code === 'CONTACT_ALREADY_EXISTS') changes = { contact: true };
          else if (failure.code === 'CONTACT_REQUEST_PENDING') changes = { contactRequestPending: true };
          else throw failure;
        }
      }
      if (active.current) setProfile(current => ({ ...current, ...changes }));
    } catch (failure) { if (active.current) setError(failure.message || (ar ? 'تعذر إتمام الإجراء.' : 'Could not complete the action.')); }
    finally { pending.current = false; if (active.current) setBusy(false); }
  };
  const interact = actor && profile && actor !== profile.id && !profile.blockedByMe && !profile.teamAccount;
  return <DetailDialog title={ar ? 'لمحة عن الملف' : 'Profile preview'} theme={theme} language={language} onClose={onClose} className="chat-profile-preview">{close => <>
    <div className="chat-preview-person">
      <button type="button" className="chat-preview-avatar-link" aria-label={ar ? `فتح ملف ${name}` : `Open ${name}'s profile`} onClick={() => close(() => onOpenProfile(value.userHandle || id))}>
        <FramedAvatar src={value.avatarUrl || value.avatar} alt={name} sensitive={value.sensitiveAvatar} size={88} frameUrl={value.frameUrl} frameBorderRadius={value.frameBorderRadius} frameFit={value.frameFit}/>
      </button>
      <div className="chat-preview-identity"><div className="chat-preview-name-row"><h3 dir="auto">{name}</h3>{value.verified && <img className="chat-preview-verified" src="/assets/images/global/verified.png" width={22} height={22} alt={ar ? 'حساب موثق' : 'Verified account'}/>}</div>{value.userHandle && <p dir="ltr">@{value.userHandle}</p>}</div>
    </div>
    {profile && !profile.blockedByMe && <PreviewBadges badges={profile.badges || []} followsMe={profile.followsMe} theme={theme} language={language}/>}
    {!profile && !error && <p className="chat-detail-note" role="status">{ar ? 'جارٍ تحميل التفاصيل…' : 'Loading details…'}</p>}
    {profile && actor !== profile.id && <div className="chat-preview-actions" aria-busy={busy}>
      {interact && <>
      <button type="button" className="chat-detail-primary" disabled={busy || (!profile.followedByMe && wait > 0)} onClick={() => action('follow')}>{profile.followedByMe ? <Check size={18}/> : <UserPlus size={18}/>}<span>{profile.followedByMe ? (ar ? 'إلغاء المتابعة' : 'Unfollow') : wait ? followWaitLabel(wait, ar) : (ar ? 'متابعة' : 'Follow')}</span></button>
      <button type="button" className="chat-detail-secondary" disabled={busy || profile.contact || profile.contactRequestPending} onClick={() => action('contact')}>{profile.contact || profile.contactRequestPending ? <Check size={18}/> : <Users size={18}/>}<span>{profile.contact ? (ar ? 'من معارفك' : 'Added contact') : profile.contactRequestPending ? (ar ? 'تم إرسال الطلب' : 'Request sent') : (ar ? 'إضافة معرفة' : 'Add contact')}</span></button>
      </>}
      <ProfileWallet userId={profile.id} language={language}/>
    </div>}
    {error && <p className="chat-detail-error" role="alert">{error}</p>}
    <div className="chat-preview-footer">
    <button type="button" className="chat-preview-profile-link" onClick={() => close(() => onOpenProfile(value.userHandle || id))}>{ar ? 'عرض الملف الشخصي' : 'Open full profile'}{ar ? <ArrowUpLeft size={17}/> : <ArrowUpRight size={17}/>}</button>

    </div>
  </>}</DetailDialog>;
}
export default function ProfilePreview(props) {
  const { currentUserId } = useAuth();
  return <Preview key={`${currentUserId}:${props.person.userId || props.person.id}`} {...props} actor={currentUserId}/>;
}
